import React, { useState, useMemo, useEffect } from 'react';
import { 
  Trophy, Award, Utensils, Shield, Flame, Sparkles, Heart, 
  Lock, CheckCircle2, Star, Target, Droplets, ChevronDown, 
  ChevronUp, Crown, Zap, Coffee, Check, Gift, Smile, Info,
  PackageCheck, ShoppingBag, Coins, ToggleLeft, ToggleRight,
  Boxes, Sparkle, ArrowRight
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { cn } from '../lib/utils';
import canvasConfetti from 'canvas-confetti';
import { 
  HistoryEntry, 
  WaterLog, 
  Language, 
  BioBoxInventoryItem,
  BioCrateInventoryItem, 
  BioBoxRarity, 
  BioCrateRarity,
  LootItem, 
  GamificationProfile 
} from '../types';
import { 
  DEMOGRAPHIC_TITLES_MATRIX, 
  PERKS_REGISTRY, 
  BIO_BOX_DEFINITIONS,
  BIO_CRATE_DEFINITIONS,
  calculateDemographicCohort, 
  resolveDemographicTitle 
} from '../data/gamificationEconomyConfig';
import { 
  getUserBioBoxes, 
  getGamificationProfile, 
  awardBioBox 
} from '../services/bioBoxService';
import { BioBoxUnboxingModal } from './BioBoxUnboxingModal';
import { RedemptionVault } from './RedemptionVault';
import { auth } from '../services/firebase';
import { sfx } from '../services/audioSynthesis';

const localConfetti = canvasConfetti.create(null, { useWorker: false, resize: true });

interface AchievementsPanelProps {
  history: HistoryEntry[];
  waterLogs: WaterLog[];
  consecutiveDaysStreak: number;
  mutualLikesCount: number;
  subscriptionTier: 'free' | 'pro' | 'premium' | 'premium_pro';
  isTrialActive: boolean;
  lang: Language;
  theme: 'light' | 'dark';
  skin: 'stalker' | string;
  vibe: 'casual' | string;
  isRankExpanded: boolean;
  setIsRankExpanded: (val: boolean) => void;
  isBadgesExpanded: boolean;
  setIsBadgesExpanded: (val: boolean) => void;
  toast: {
    success: (msg: string, opt?: any) => void;
    error: (msg: string, opt?: any) => void;
    info: (msg: string, opt?: any) => void;
  };
  userId?: string;
  userAge?: number;
  userGender?: string;
  onThemeChanged?: (themeId: string) => void;
}

export const AchievementsPanel: React.FC<AchievementsPanelProps> = ({
  history,
  waterLogs,
  consecutiveDaysStreak,
  mutualLikesCount,
  subscriptionTier,
  isTrialActive,
  lang,
  theme,
  skin,
  vibe,
  isRankExpanded,
  setIsRankExpanded,
  isBadgesExpanded,
  setIsBadgesExpanded,
  toast,
  userId,
  userAge,
  userGender,
  onThemeChanged
}) => {
  // Top-level Navigation Mode Tab
  const [mainSection, setMainSection] = useState<'titles' | 'crates' | 'vault' | 'badges'>('titles');
  const [badgeFilterTab, setBadgeFilterTab] = useState<'all' | 'nutrition' | 'hydration' | 'premium'>('all');
  
  // Tone preference: 'bro' vs 'system'
  const [tone, setTone] = useState<'bro' | 'system'>('bro');

  // Bio-Boxes & Gamification Profile State
  const [profile, setProfile] = useState<GamificationProfile | null>(null);
  const [crates, setCrates] = useState<BioBoxInventoryItem[]>([]);
  const [selectedCrateForUnbox, setSelectedCrateForUnbox] = useState<BioBoxInventoryItem | null>(null);
  const [isUnboxingModalOpen, setIsUnboxingModalOpen] = useState<boolean>(false);

  const activeUid = userId || auth.currentUser?.uid;
  const isUA = lang === 'ua' || (lang as string).startsWith('uk');

  // Refresh profile and boxes
  const refreshGamificationData = async () => {
    if (!activeUid) return;
    try {
      const p = await getGamificationProfile(activeUid);
      const c = await getUserBioBoxes(activeUid);
      setProfile(p);
      setCrates(c);
      if (p?.tone_preference) {
        setTone(p.tone_preference);
      }
    } catch (e) {
      console.warn('[AchievementsPanel] Error refreshing gamification data:', e);
    }
  };

  useEffect(() => {
    refreshGamificationData();

    const handleUpdate = () => {
      refreshGamificationData();
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('gamification-profile-updated', handleUpdate);
      window.addEventListener('bio-boxes-updated', handleUpdate);
    }

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('gamification-profile-updated', handleUpdate);
        window.removeEventListener('bio-boxes-updated', handleUpdate);
      }
    };
  }, [activeUid]);

  // Calculates total water drank
  const totalWaterMl = useMemo(() => {
    return waterLogs.reduce((acc, w) => acc + (w.amount_ml || 0), 0);
  }, [waterLogs]);

  // Demographic Title Calculation
  const totalLogs = history.length + waterLogs.length;
  const cohort = useMemo(() => {
    return calculateDemographicCohort(userAge);
  }, [userAge]);

  const activeTitle = useMemo(() => {
    return resolveDemographicTitle(cohort, userGender || 'male', history.length);
  }, [cohort, userGender, history.length]);

  // Derive copywriting tone from profile or vibe prop ('casual' -> 'bro', 'standard' -> 'system')
  const activeTone = profile?.tone_preference || (vibe === 'casual' ? 'bro' : tone);

  // Active Perks derived from unlocked titles
  const activePerksList = useMemo(() => {
    const perks: typeof PERKS_REGISTRY[string][] = [];
    activeTitle.perks.forEach(perkId => {
      if (PERKS_REGISTRY[perkId]) {
        perks.push(PERKS_REGISTRY[perkId]);
      }
    });
    return perks;
  }, [activeTitle]);

  // Builds the fully-linked dynamic badges list
  const badgesList = useMemo(() => {
    const isPro = subscriptionTier === 'pro' || subscriptionTier === 'premium' || subscriptionTier === 'premium_pro';
    const isPremium = subscriptionTier === 'premium' || subscriptionTier === 'premium_pro';

    return [
      // --- Nutrition Milestones ---
      {
        id: 'tier1',
        type: 'nutrition',
        icon: Utensils,
        title: isUA ? 'Початківець' : 'Initiate',
        desc: isUA ? 'Записати першу страву в журнал' : 'Record your first meal entry',
        target: 1,
        current: history.length,
        unlocked: history.length >= 1,
        color: 'text-emerald-500',
        bg: 'bg-emerald-500/10',
        border: 'border-emerald-500/30'
      },
      {
        id: 'tier2',
        type: 'nutrition',
        icon: Shield,
        title: isUA ? 'Майстер Макросу' : 'Macro Manager',
        desc: isUA ? 'Записати 3+ страви у журнал' : 'Record 3+ meal entries',
        target: 3,
        current: history.length,
        unlocked: history.length >= 3,
        color: 'text-blue-500',
        bg: 'bg-blue-500/10',
        border: 'border-blue-500/30'
      },
      {
        id: 'tier3',
        type: 'nutrition',
        icon: Flame,
        title: isUA ? 'Сигма Мезоморф' : 'Sigma Shredder',
        desc: isUA ? 'Записати 10+ страв у журнал' : 'Record 10+ meal entries',
        target: 10,
        current: history.length,
        unlocked: history.length >= 10,
        color: 'text-purple-500',
        bg: 'bg-purple-500/10',
        border: 'border-purple-500/30'
      },
      {
        id: 'tier4',
        type: 'nutrition',
        icon: Sparkles,
        title: isUA ? 'Апекс Архітектор' : 'Apex Architect',
        desc: isUA ? 'Записати 25+ страв у журнал' : 'Record 25+ meal entries',
        target: 25,
        current: history.length,
        unlocked: history.length >= 25,
        color: 'text-indigo-500 dark:text-indigo-400',
        bg: 'animate-premium-gradient-bg text-indigo-900 dark:text-indigo-200',
        border: 'border-indigo-500/30 dark:border-purple-500/30'
      },

      // --- Hydration Milestones ---
      {
        id: 'water_init',
        type: 'hydration',
        icon: Droplets,
        title: isUA ? 'Чисте Джерело' : 'Clean Spring',
        desc: isUA ? 'Записати першу воду або напій' : 'Log your first beverage',
        target: 1,
        current: waterLogs.length,
        unlocked: waterLogs.length >= 1,
        color: 'text-cyan-500',
        bg: 'bg-cyan-500/10',
        border: 'border-cyan-500/30'
      },
      {
        id: 'water_volume_1l',
        type: 'hydration',
        icon: Droplets,
        title: isUA ? 'Аква-Рейнджер' : 'Aqua Ranger',
        desc: isUA ? 'Випити загалом 1,000+ мл води' : 'Consume over 1,000ml of water',
        target: 1000,
        current: totalWaterMl,
        unlocked: totalWaterMl >= 1000,
        color: 'text-sky-500',
        bg: 'bg-sky-500/10',
        border: 'border-sky-500/30'
      },
      {
        id: 'water_volume_5l',
        type: 'hydration',
        icon: Trophy,
        title: isUA ? 'Володар Потоку' : 'Flow Overlord',
        desc: isUA ? 'Випити загалом 5,000+ мл води' : 'Consume over 5,000ml of water',
        target: 5000,
        current: totalWaterMl,
        unlocked: totalWaterMl >= 5000,
        color: 'text-teal-500',
        bg: 'bg-teal-500/10',
        border: 'border-teal-500/30'
      },

      // --- Streak & Consistency Milestones ---
      {
        id: 'streak_3',
        type: 'nutrition',
        icon: Flame,
        title: isUA ? '3-Денний Вогонь' : '3-Day Fire',
        desc: isUA ? 'Утримувати 3 дні поспіль' : 'Log meals 3 consecutive days',
        target: 3,
        current: consecutiveDaysStreak,
        unlocked: consecutiveDaysStreak >= 3,
        color: 'text-orange-500',
        bg: 'bg-orange-500/10',
        border: 'border-orange-500/20'
      },
      {
        id: 'streak_7',
        type: 'nutrition',
        icon: Zap,
        title: isUA ? '7-Денна Іскра' : '7-Day Spark',
        desc: isUA ? 'Утримувати 7 днів поспіль' : 'Log meals 7 consecutive days',
        target: 7,
        current: consecutiveDaysStreak,
        unlocked: consecutiveDaysStreak >= 7,
        color: 'text-yellow-500',
        bg: 'bg-yellow-500/10',
        border: 'border-yellow-500/20'
      },
      {
        id: 'streak_14',
        type: 'nutrition',
        icon: Star,
        title: isUA ? '14-Денна Зірка' : '14-Day Star',
        desc: isUA ? 'Утримувати 14 днів поспіль' : 'Log meals 14 consecutive days',
        target: 14,
        current: consecutiveDaysStreak,
        unlocked: consecutiveDaysStreak >= 14,
        color: 'text-emerald-500',
        bg: 'bg-emerald-500/10',
        border: 'border-emerald-500/20'
      },

      // --- Community (Likes) Milestones ---
      {
        id: 'mutual_likes_1',
        type: 'nutrition',
        icon: Heart,
        title: isUA ? 'Перша Іскра' : 'First Spark',
        desc: isUA ? 'Отримати 1+ взаємний лайк у чаті' : 'Get 1+ mutual chat like',
        target: 1,
        current: mutualLikesCount,
        unlocked: mutualLikesCount >= 1,
        color: 'text-rose-500',
        bg: 'bg-rose-500/10',
        border: 'border-rose-500/30'
      },
      {
        id: 'mutual_likes_5',
        type: 'nutrition',
        icon: Heart,
        title: isUA ? 'Бро-Хімія' : 'Bro-Chemistry',
        desc: isUA ? 'Отримати 5+ взаємних лайків' : 'Get 5+ mutual chat likes',
        target: 5,
        current: mutualLikesCount,
        unlocked: mutualLikesCount >= 5,
        color: 'text-pink-500',
        bg: 'bg-pink-500/10',
        border: 'border-pink-500/30'
      },

      // --- Premium & Special Tiers Milestones ---
      {
        id: 'sub_trial',
        type: 'premium',
        icon: Gift,
        title: isUA ? 'Бро-Першопроходець' : 'Bro-Trialblazer',
        desc: isUA ? 'Активовано 2-денний тестовий період' : 'Claimed 2-Day Premium Trial',
        target: 1,
        current: (isTrialActive || subscriptionTier === 'premium' || subscriptionTier === 'premium_pro') ? 1 : 0,
        unlocked: isTrialActive || subscriptionTier === 'premium' || subscriptionTier === 'premium_pro',
        color: 'text-emerald-500',
        bg: 'bg-emerald-500/10',
        border: 'border-emerald-500/30'
      },
      {
        id: 'sub_pro',
        type: 'premium',
        icon: Shield,
        title: isUA ? 'Патруль Зони' : 'PRO Pioneer',
        desc: isUA ? 'Отримано активну PRO підписку' : 'Unlock PRO membership',
        target: 1,
        current: isPro ? 1 : 0,
        unlocked: isPro,
        color: 'text-amber-500',
        bg: 'bg-amber-500/10',
        border: 'border-amber-500/30'
      },
      {
        id: 'sub_premium',
        type: 'premium',
        icon: Trophy,
        title: isUA ? 'Легенда Біо-Хакінгу' : 'Bio-Hack Legend',
        desc: isUA ? 'Отримано активну PREMIUM підписку' : 'Unlock Bio-Hacking PREMIUM membership',
        target: 1,
        current: isPremium ? 1 : 0,
        unlocked: isPremium,
        color: 'text-purple-500',
        bg: 'bg-purple-500/10',
        border: 'border-purple-500/30'
      }
    ];
  }, [history, waterLogs, totalWaterMl, consecutiveDaysStreak, mutualLikesCount, subscriptionTier, isTrialActive, isUA]);

  // Overall count summary
  const totalBadgesCount = badgesList.length;
  const unlockedBadgesCount = useMemo(() => {
    return badgesList.filter(b => b.unlocked).length;
  }, [badgesList]);

  const progressPercent = Math.round((unlockedBadgesCount / totalBadgesCount) * 100);

  const handleOpenCrateModal = (crate: BioCrateInventoryItem) => {
    sfx.playUiClick();
    setSelectedCrateForUnbox(crate);
    setIsUnboxingModalOpen(true);
  };

  const isStalker = skin === 'stalker';
  const isLight = theme === 'light' && !isStalker;

  const handleBadgeClick = (badge: typeof badgesList[0]) => {
    sfx.playUiClick();
    if (badge.unlocked) {
      sfx.playRarityFanfare('epic');
      localConfetti({
        particleCount: 100,
        spread: 80,
        origin: { y: 0.6 },
        colors: isStalker ? ['#4ade80', '#86efac', '#22c55e', '#f97316'] :
                ['#10B981', '#059669', '#34D399', '#6EE7B7', '#F59E0B']
      });
      toast.success(
        isUA ? `🏆 БЕЙДЖ РОЗБЛОКОВАНО: ${badge.title}` : `🏆 UNLOCKED: ${badge.title}`,
        { description: badge.desc }
      );
    } else {
      const remaining = Math.max(0, badge.target - badge.current);
      let tips = '';
      if (badge.type === 'nutrition') {
        tips = isUA 
          ? `Залишилось записати ще ${remaining} одиниць прогресу.` 
          : `Need ${remaining} more progress units to unlock this milestone.`;
      } else if (badge.type === 'hydration') {
        tips = isUA
          ? `Залишилось випити ще ${remaining} мл рідини.`
          : `Drink ${remaining}ml more fluid to unlock this reward.`;
      } else if (badge.type === 'premium') {
        tips = isUA
          ? "Перейдіть до розділу Членства нижче та активуйте Premium / PRO плани."
          : "Go to the membership block below and upgrade your tier.";
      }
      toast.info(
        isUA ? `🔒 БЕЙДЖ ЗАБЛОКОВАНО: ${badge.title}` : `🔒 LOCKED: ${badge.title}`,
        { description: `${badge.desc}. ${tips}` }
      );
    }
  };

  return (
    <div className={cn(
      "p-4 sm:p-5 rounded-3xl border transition-all card-shadow",
      isStalker ? "border-[#4ade80]/30 bg-black font-mono text-[#4ade80]" :
      "bg-[var(--card-bg)] border-[var(--card-border)] text-[var(--text-main)] shadow-xs"
    )} id="achievements-dashboard-wrapper">
      
      {/* MAIN EXPANSION TOGGLE HEADER */}
      <button
        type="button"
        onClick={() => {
          sfx.playUiClick();
          setIsRankExpanded(!isRankExpanded);
        }}
        className="w-full flex items-center justify-between gap-2.5 text-left cursor-pointer group"
      >
        <div className="flex items-center gap-2.5 min-w-0">
          <div className={cn(
            "w-8 h-8 sm:w-9 sm:h-9 rounded-xl flex items-center justify-center shrink-0 shadow-xs border",
            isStalker ? "bg-black border-[#4ade80]/40 text-[#4ade80]" :
            "bg-emerald-500/10 border-emerald-500/20 text-emerald-600 dark:text-emerald-400"
          )}>
            <Trophy className="w-4 h-4 sm:w-5 sm:h-5" />
          </div>
          <div className="min-w-0">
            <h4 className={cn(
              "font-extrabold text-xs sm:text-sm uppercase tracking-wider truncate",
              isStalker ? "text-[#4ade80] font-mono" :
              "text-zinc-900 dark:text-zinc-100 font-sans tracking-tight"
            )}>
              {skin === 'stalker' 
                ? (isUA ? 'РЕЄСТР ДОСЯГНЕНЬ ТА СТАШУ' : 'ACHIEVEMENTS & STASH RECORD') 
                : (isUA ? 'ДОСЯГНЕННЯ ТА ВИНАГОРОДИ' : 'ACHIEVEMENTS & REWARDS')}
            </h4>
            <p className={cn(
              "text-[11px] sm:text-xs truncate font-sans",
              isStalker ? "text-[#4ade80]/70 font-mono" :
              "text-zinc-500 dark:text-zinc-400"
            )}>
              {skin === 'stalker'
                ? (isUA ? 'Біометричні досягнення детекторів' : 'Zone survival biological status')
                : (isUA ? 'Титули, скрині припасів, токени та пропуски' : 'Demographic titles, supply crates, and vault tokens')}
            </p>
          </div>
        </div>
        <div className={cn(
          "transition-colors shrink-0 ml-auto",
          isStalker ? "text-[#4ade80]/60 group-hover:text-[#4ade80]" :
          "text-zinc-400 group-hover:text-zinc-600 dark:group-hover:text-zinc-200"
        )}>
          {isRankExpanded ? <ChevronUp className="w-4 h-4 sm:w-5 sm:h-5" /> : <ChevronDown className="w-4 h-4 sm:w-5 sm:h-5" />}
        </div>
      </button>

      {/* EXPANDED CONTENT BODY */}
      {isRankExpanded && (
        <div className={cn(
          "space-y-5 mt-4 pt-4 border-t animate-fadeIn",
          isStalker ? "border-[#4ade80]/20" :
          "border-zinc-200 dark:border-zinc-800"
        )}>
          
          {/* PRIMARY SECTION SELECTOR TABS */}
          <div className={cn(
            "grid grid-cols-2 sm:grid-cols-4 gap-1.5 p-1 rounded-2xl border",
            isStalker ? "bg-black border-[#4ade80]/30 font-mono" :
            "bg-zinc-100 dark:bg-zinc-800/80 border-zinc-200 dark:border-zinc-700/60 font-sans"
          )}>
            {[
              { id: 'titles', label: isUA ? 'Титули & Перки' : 'Titles & Perks', icon: Crown },
              { id: 'crates', label: isUA ? `Біо-Бокси (${crates.length})` : `Bio-Boxes (${crates.length})`, icon: PackageCheck },
              { id: 'vault', label: isUA ? 'Сховище Обміну' : 'Redemption Vault', icon: ShoppingBag },
              { id: 'badges', label: isUA ? 'Каталог Бейджів' : 'Badges & Stats', icon: Award }
            ].map(tab => {
              const Icon = tab.icon;
              const isActive = mainSection === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => {
                    sfx.playUiClick();
                    setMainSection(tab.id as any);
                  }}
                  className={cn(
                    "flex items-center justify-center gap-1.5 py-2 px-2.5 rounded-xl text-xs font-bold uppercase tracking-wider transition-all cursor-pointer",
                    isActive
                      ? (isStalker ? "bg-[#4ade80]/20 text-[#4ade80] border border-[#4ade80] font-mono" :
                         "bg-emerald-500 text-white shadow-sm font-semibold")
                      : (isStalker ? "text-[#4ade80]/60 hover:text-[#4ade80] hover:bg-[#4ade80]/10 font-mono" :
                         "text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 hover:bg-zinc-200/60 dark:hover:bg-zinc-700/60 font-medium")
                  )}
                >
                  <Icon className="w-3.5 h-3.5 shrink-0" />
                  <span className="truncate">{tab.label}</span>
                </button>
              );
            })}
          </div>

          {/* ========================================================== */}
          {/* TAB 1: DYNAMIC TITLES & ACTIVE PERKS MATRIX                */}
          {/* ========================================================== */}
          {mainSection === 'titles' && (
            <div className="space-y-4 animate-fadeIn">
              
              {/* Active Rank Card */}
              <div className={cn(
                "p-5 rounded-2xl border flex flex-col sm:flex-row items-center justify-between gap-4",
                isStalker ? "border-[#4ade80]/30 bg-black" :
                "border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 shadow-xs"
              )}>
                
                {/* Title Info */}
                <div className="space-y-1.5 text-center sm:text-left">
                  <div className="flex items-center justify-center sm:justify-start gap-2">
                    <span className={cn(
                      "text-[10px] font-bold uppercase tracking-widest",
                      isStalker ? "font-mono text-[#4ade80]" :
                      "text-emerald-600 dark:text-emerald-400"
                    )}>
                      {isUA ? `ДЕМОГРАФІЧНА КОГОРТА: ${cohort.toUpperCase()}` : `DEMOGRAPHIC COHORT: ${cohort.toUpperCase()}`}
                    </span>
                  </div>

                  <h3 className={cn(
                    "text-2xl font-black uppercase tracking-wider flex items-center justify-center sm:justify-start gap-2",
                    isStalker ? "font-mono text-[#4ade80]" :
                    "text-zinc-900 dark:text-white font-sans"
                  )}>
                    <Crown className={cn(
                      "w-6 h-6",
                      isStalker ? "text-[#4ade80]" :
                      "text-amber-500 dark:text-amber-400"
                    )} />
                    <span className={isStalker ? "text-[#4ade80]" : "text-emerald-600 dark:text-emerald-400 font-bold"}>
                      {isUA ? activeTitle.title.ua : activeTitle.title.en}
                    </span>
                  </h3>

                  <p className={cn(
                    "text-xs max-w-md leading-relaxed font-sans",
                    isStalker ? "text-[#4ade80]/80 font-mono" :
                    "text-zinc-600 dark:text-zinc-400"
                  )}>
                    {activeTone === 'bro'
                      ? (isUA ? activeTitle.broDesc.ua : activeTitle.broDesc.en)
                      : (isUA ? activeTitle.systemDesc.ua : activeTitle.systemDesc.en)}
                  </p>
                </div>

              </div>

              {/* Active Mechanical Perks Grid */}
              <div className="space-y-2.5">
                <div className="flex items-center gap-2">
                  <Sparkles className={cn(
                    "w-4 h-4",
                    isStalker ? "text-[#4ade80]" :
                    "text-amber-500 dark:text-amber-400"
                  )} />
                  <h4 className={cn(
                    "text-xs font-bold uppercase tracking-wider",
                    isStalker ? "font-mono text-[#4ade80]" :
                    "text-zinc-900 dark:text-zinc-100 font-sans"
                  )}>
                    {isUA ? 'ВАШІ АКТИВНІ ПЕРКИ ТА БОНУСИ' : 'YOUR ACTIVE MECHANICAL PERKS'}
                  </h4>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  {activePerksList.map(perk => (
                    <div 
                      key={perk.id}
                      className={cn(
                        "p-3.5 rounded-xl border flex items-start gap-3 transition-all",
                        isStalker ? "border-[#4ade80]/30 bg-black hover:border-[#4ade80]/60 font-mono" :
                        "border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 hover:border-emerald-500/40 shadow-xs font-sans"
                      )}
                    >
                      <div className={cn(
                        "w-9 h-9 rounded-lg border flex items-center justify-center shrink-0",
                        isStalker ? "bg-[#4ade80]/15 border-[#4ade80]/40 text-[#4ade80]" :
                        "bg-amber-500/10 border-amber-500/20 text-amber-600 dark:text-amber-400"
                      )}>
                        <Zap className="w-4 h-4" />
                      </div>
                      <div className="space-y-0.5 min-w-0">
                        <h5 className={cn(
                          "font-bold text-xs truncate",
                          isStalker ? "font-mono text-[#4ade80]" :
                          "text-zinc-900 dark:text-white font-sans"
                        )}>
                          {isUA ? perk.name.ua : perk.name.en}
                        </h5>
                        <p className={cn(
                          "text-[11px] leading-snug font-sans",
                          isStalker ? "text-[#4ade80]/80 font-mono" :
                          "text-zinc-500 dark:text-zinc-400"
                        )}>
                          {tone === 'bro'
                            ? (isUA ? perk.broFlavor.ua : perk.broFlavor.en)
                            : (isUA ? perk.desc.ua : perk.desc.en)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

            </div>
          )}

          {/* ========================================================== */}
          {/* TAB 2: BIO-BOXES & UNBOXING TRAY                          */}
          {/* ========================================================== */}
          {mainSection === 'crates' && (
            <div className="space-y-4 animate-fadeIn">
              
              {/* Bio-Boxes Banner */}
              <div className={cn(
                "p-4 sm:p-5 rounded-2xl border flex flex-col sm:flex-row items-center justify-between gap-4",
                isStalker ? "border-[#4ade80]/30 bg-black font-mono" :
                "border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 text-zinc-900 dark:text-white font-sans shadow-xs"
              )}>
                <div className="space-y-1 text-center sm:text-left">
                  <div className="flex items-center justify-center sm:justify-start gap-2">
                    <PackageCheck className={cn(
                      "w-4 h-4",
                      isStalker ? "text-[#4ade80]" :
                      "text-emerald-600 dark:text-emerald-400"
                    )} />
                    <h4 className={cn(
                      "text-xs sm:text-sm font-bold uppercase tracking-wider",
                      isStalker ? "font-mono text-[#4ade80]" :
                      "text-zinc-900 dark:text-white font-sans tracking-tight"
                    )}>
                      {isUA ? 'ІНВЕНТАР БІО-БОКСІВ ТА ПРИПАСІВ' : 'BIO-BOX LOOT INVENTORY'}
                    </h4>
                  </div>
                  <p className={cn(
                    "text-xs font-sans leading-relaxed max-w-md",
                    isStalker ? "text-[#4ade80]/80 font-mono" :
                    "text-zinc-600 dark:text-zinc-400"
                  )}>
                    {isUA 
                      ? 'Отримуйте щоденні біо-бокси за прийоми їжі, стрік-бокси за дисципліну та сейфи за точні макроси!'
                      : 'Earn Daily Bio-Boxes for meals, Streak Boxes for discipline, and Perfect Vaults for macro accuracy!'}
                  </p>
                </div>

                <div className={cn(
                  "px-3.5 py-1.5 rounded-xl border text-xs font-bold",
                  isStalker ? "bg-black border-[#4ade80]/40 text-[#4ade80] font-mono" :
                  "bg-emerald-50 dark:bg-emerald-950/50 border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-400 font-sans"
                )}>
                  {crates.length} {isUA ? 'боксів готово' : 'boxes ready'}
                </div>
              </div>

              {/* Unopened Bio-Boxes Grid */}
              {crates.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3.5">
                  {crates.map(c => {
                    const targetType = c.boxType || c.crateType || 'daily_supply';
                    const meta = BIO_BOX_DEFINITIONS[targetType] || BIO_BOX_DEFINITIONS.daily_supply;
                    return (
                      <div
                        key={c.id}
                        className={cn(
                          "p-4 rounded-2xl border flex flex-col justify-between space-y-3 transition-all group",
                          isStalker ? "border-[#4ade80]/30 bg-black hover:border-[#4ade80] font-mono" :
                          "border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 hover:border-emerald-500/50 dark:hover:border-emerald-500/50 hover:shadow-md text-zinc-900 dark:text-white font-sans"
                        )}
                      >
                        <div className="flex items-center gap-3">
                          <div 
                            className={cn(
                              "w-12 h-12 rounded-xl border flex items-center justify-center shrink-0",
                              isStalker ? "bg-black border-[#4ade80]/40 text-[#4ade80]" :
                              "bg-zinc-50 dark:bg-zinc-800 border-zinc-200 dark:border-zinc-700"
                            )}
                          >
                            {targetType === 'perfect_macro' ? (
                              <Crown className={cn("w-6 h-6", isStalker ? "text-[#4ade80]" : "text-emerald-500 dark:text-emerald-400")} />
                            ) : targetType === 'streak_matrix' ? (
                              <Flame className={cn("w-6 h-6", isStalker ? "text-[#f97316]" : "text-amber-500 dark:text-amber-400")} />
                            ) : (
                              <PackageCheck className={cn("w-6 h-6", isStalker ? "text-[#4ade80]" : "text-emerald-500 dark:text-emerald-400")} />
                            )}
                          </div>

                          <div className="min-w-0">
                            <h5 className={cn(
                              "font-bold text-xs truncate",
                              isStalker ? "font-mono text-[#4ade80]" :
                              "text-zinc-900 dark:text-zinc-100 font-sans"
                            )}>
                              {isUA ? meta.name.ua : meta.name.en}
                            </h5>
                            <span className={cn(
                              "text-[10px] block truncate font-sans",
                              isStalker ? "font-mono text-[#4ade80]/70" :
                              "text-zinc-500 dark:text-zinc-400"
                            )}>
                              {c.source}
                            </span>
                          </div>
                        </div>

                        <button
                          type="button"
                          onClick={() => handleOpenCrateModal(c)}
                          className={cn(
                            "w-full py-2.5 px-3 rounded-xl font-bold text-xs uppercase tracking-wider transition-all cursor-pointer flex items-center justify-center gap-1.5",
                            isStalker ? "bg-[#4ade80]/20 hover:bg-[#4ade80]/30 border border-[#4ade80] text-[#4ade80] font-mono shadow-md hover:scale-[1.02] active:scale-95" :
                            "bg-emerald-500 hover:bg-emerald-600 text-white font-sans shadow-sm hover:scale-[1.02] active:scale-95"
                          )}
                        >
                          <Zap className="w-3.5 h-3.5 fill-current" />
                          <span>
                            {isStalker ? (isUA ? 'ВІДКРИТИ КОНТЕЙНЕР' : 'OPEN SUPPLY CRATE') :
                             (isUA ? 'РОЗПАКУВАТИ БІО-БОКС' : 'UNBOX BIO-BOX')}
                          </span>
                        </button>
                      </div>
                    );
                  })}
                </div>
              ) : (
                /* Empty Bio-Box State */
                <div className={cn(
                  "p-8 rounded-2xl border border-dashed text-center space-y-2",
                  isStalker ? "border-[#4ade80]/30 bg-black text-[#4ade80]/80 font-mono" :
                  "border-zinc-200 dark:border-zinc-800 bg-zinc-50/50 dark:bg-zinc-900/30 text-zinc-700 dark:text-zinc-300 font-sans"
                )}>
                  <PackageCheck className={cn(
                    "w-10 h-10 mx-auto",
                    isStalker ? "text-[#4ade80]/40" :
                    "text-zinc-400 dark:text-zinc-500"
                  )} />
                  <h5 className={cn(
                    "text-sm font-bold uppercase",
                    isStalker ? "font-mono text-[#4ade80]" :
                    "text-zinc-800 dark:text-zinc-200 font-sans"
                  )}>
                    {isUA ? 'Немає невідкритих боксів' : 'No Unopened Bio-Boxes'}
                  </h5>
                  <p className={cn(
                    "text-xs max-w-sm mx-auto font-sans leading-relaxed",
                    isStalker ? "text-[#4ade80]/60 font-mono" :
                    "text-zinc-500 dark:text-zinc-400"
                  )}>
                    {isUA 
                      ? 'Запишіть прийом їжі або рідини сьогодні, щоб отримати Щоденний Біо-Бокс Припасів!' 
                      : 'Log a meal or beverage today to unlock your Daily Supply Bio-Box!'}
                  </p>
                </div>
              )}

            </div>
          )}

          {/* ========================================================== */}
          {/* TAB 3: REDEMPTION VAULT (TOKEN ECONOMY SHOP)              */}
          {/* ========================================================== */}
          {mainSection === 'vault' && (
            <RedemptionVault
              userId={activeUid}
              lang={lang}
              vibe={vibe}
              tone={tone}
              theme={theme}
              skin={skin}
              toast={toast}
              onBalanceUpdated={(newBal) => {
                if (profile) {
                  setProfile({ ...profile, token_balance: newBal });
                }
              }}
              onThemeChanged={onThemeChanged}
            />
          )}

          {/* ========================================================== */}
          {/* TAB 4: BADGES & STATS                                     */}
          {/* ========================================================== */}
          {mainSection === 'badges' && (
            <div className="space-y-4 animate-fadeIn">
              
              {/* Badges Progress Summary */}
              <div className={cn(
                "grid grid-cols-1 sm:grid-cols-12 gap-4 p-4 rounded-2xl border",
                isStalker ? "border-[#4ade80]/30 bg-black font-mono" :
                "border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 text-zinc-900 dark:text-white font-sans shadow-xs"
              )}>
                <div className={cn(
                  "col-span-1 sm:col-span-4 flex flex-col items-center justify-center text-center p-1 border-r-0 sm:border-r",
                  isStalker ? "border-[#4ade80]/30" :
                  "border-zinc-200 dark:border-zinc-800"
                )}>
                  <div className="relative w-20 h-20 flex items-center justify-center">
                    <svg className="w-full h-full transform -rotate-90">
                      <circle cx="40" cy="40" r="34" stroke="currentColor" strokeWidth="5" fill="transparent" className={isLight ? "text-zinc-200" : "text-zinc-800"} />
                      <circle
                        cx="40" cy="40" r="34" stroke="currentColor" strokeWidth="5" fill="transparent"
                        strokeDasharray={2 * Math.PI * 34}
                        strokeDashoffset={2 * Math.PI * 34 * (1 - progressPercent / 100)}
                        className={cn(
                          "transition-all duration-700 ease-out",
                          isStalker ? "text-[#4ade80]" : "text-emerald-500"
                        )}
                      />
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <span className={cn(
                        "text-lg font-black",
                        isStalker ? "font-mono" : "font-sans"
                      )}>{unlockedBadgesCount}/{totalBadgesCount}</span>
                      <span className={cn(
                        "text-[8px] uppercase tracking-wider font-sans",
                        isStalker ? "text-[#4ade80]/70 font-mono" :
                        "text-zinc-500 dark:text-zinc-400"
                      )}>
                        {isUA ? 'БЕЙДЖІВ' : 'BADGES'}
                      </span>
                    </div>
                  </div>
                  <p className={cn(
                    "text-[10px] mt-2 font-bold uppercase tracking-wider",
                    isStalker ? "font-mono text-[#4ade80]" :
                    "text-zinc-600 dark:text-zinc-400 font-sans"
                  )}>
                    {progressPercent}% {isUA ? 'РОЗБЛОКОВАНО' : 'UNLOCKED'}
                  </p>
                </div>

                <div className="col-span-1 sm:col-span-8 flex flex-col justify-between space-y-2">
                  <div>
                    <span className={cn(
                      "text-[9px] font-bold uppercase tracking-widest",
                      isStalker ? "font-mono text-[#4ade80]/70" :
                      "text-zinc-500 dark:text-zinc-400 font-sans"
                    )}>
                      {isUA ? 'СТАТИСТИКА ДОСЯГНЕНЬ' : 'ACHIEVEMENT METRICS'}
                    </span>
                    <h5 className={cn(
                      "text-lg font-black uppercase tracking-wider mt-0.5",
                      isStalker ? "font-mono text-[#4ade80]" :
                      "text-emerald-600 dark:text-emerald-400 font-sans"
                    )}>
                      {isUA ? 'ЛЕГЕНДАРНИЙ РЕЄСТР' : 'MASTERY MATRIX'}
                    </h5>
                    <p className={cn(
                      "text-xs leading-normal mt-1 font-sans",
                      isStalker ? "text-[#4ade80]/80 font-mono" :
                      "text-zinc-600 dark:text-zinc-400"
                    )}>
                      {isUA 
                        ? 'Відкривайте бейджі за дисципліну в їжі, контроль води та підписку для отримання токенів.'
                        : 'Unlock badges across nutrition consistency, hydration tracking, and subscriptions.'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Filtering tabs */}
              <div className={cn(
                "flex flex-wrap gap-1 border-b pb-2",
                isStalker ? "border-[#4ade80]/20 font-mono" :
                "border-zinc-200 dark:border-zinc-800 font-sans"
              )}>
                {[
                  { id: 'all', label: isUA ? 'Усі' : 'All' },
                  { id: 'nutrition', label: isUA ? 'Харчування & Стрік' : 'Nutrition & Streak' },
                  { id: 'hydration', label: isUA ? 'Вода' : 'Hydration' },
                  { id: 'premium', label: isUA ? 'Преміум' : 'Premium Boosts' }
                ].map(tab => (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => {
                      sfx.playUiClick();
                      setBadgeFilterTab(tab.id as any);
                    }}
                    className={cn(
                      "px-3 py-1.5 rounded-xl text-[10px] font-bold uppercase tracking-wider transition-all cursor-pointer border",
                      badgeFilterTab === tab.id
                        ? (isStalker ? "bg-[#4ade80]/20 text-[#4ade80] border-[#4ade80] font-mono" :
                           "bg-emerald-500 text-white border-emerald-500 font-sans font-semibold")
                        : (isStalker ? "bg-black border-[#4ade80]/20 text-[#4ade80]/60 hover:text-[#4ade80] font-mono" :
                           "bg-zinc-100 dark:bg-zinc-800 border-zinc-200 dark:border-zinc-700 text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-white font-sans")
                    )}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Badges Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                {badgesList
                  .filter(b => badgeFilterTab === 'all' || b.type === badgeFilterTab)
                  .map(badge => {
                    const Icon = badge.icon;
                    const progressRatio = Math.min(100, (badge.current / badge.target) * 100);

                    return (
                      <button
                        key={badge.id}
                        type="button"
                        onClick={() => handleBadgeClick(badge)}
                        className={cn(
                          "flex flex-col items-center justify-between p-3 rounded-xl border transition-all text-center relative group cursor-pointer min-h-[115px]",
                          badge.unlocked 
                            ? (isStalker 
                                ? "bg-[#4ade80]/10 border-[#4ade80]/40 text-[#4ade80] hover:border-[#4ade80] shadow-sm font-mono"
                                : cn(badge.bg, badge.border, "hover:scale-[1.02] active:scale-95 shadow-xs font-sans"))
                            : (isStalker
                                ? "bg-black border-[#4ade80]/20 text-[#4ade80]/50 opacity-60 hover:opacity-85 font-mono"
                                : (isLight ? "bg-zinc-100/60 border-zinc-200 opacity-60 hover:opacity-85 font-sans" : "bg-zinc-900/30 border-zinc-800/50 opacity-60 hover:opacity-85 font-sans"))
                        )}
                      >
                        <div className="relative my-auto">
                          <Icon className={cn("w-6 h-6", badge.unlocked ? (isStalker ? "text-[#4ade80]" : badge.color) : (isStalker ? "text-[#4ade80]/40" : isLight ? "text-zinc-400" : "text-zinc-500"))} />
                          {!badge.unlocked && (
                            <div className={cn(
                              "absolute -bottom-1 -right-1 p-0.5 rounded-full border",
                              isStalker ? "bg-black text-[#4ade80]/60 border-[#4ade80]/40" :
                              isLight ? "bg-zinc-200 text-zinc-500 border-zinc-300" : "bg-zinc-800 text-zinc-400 border-zinc-700"
                            )}>
                              <Lock className="w-2.5 h-2.5" />
                            </div>
                          )}
                        </div>

                        <div className="w-full space-y-1 mt-2">
                          <span className={cn(
                            "text-xs font-bold leading-tight block truncate px-0.5 uppercase tracking-wide",
                            badge.unlocked ? (isStalker ? "text-[#4ade80]" : isLight ? "text-zinc-900" : "text-white") : (isStalker ? "text-[#4ade80]/50" : "text-zinc-400 dark:text-zinc-500"),
                            isStalker ? "font-mono" : "font-sans"
                          )}>
                            {badge.title}
                          </span>

                          <div className="w-full space-y-0.5">
                            <div className={cn(
                              "flex justify-between items-center text-[8px] font-bold",
                              isStalker ? "text-[#4ade80]/70 font-mono" : "text-zinc-500 dark:text-zinc-400 font-sans"
                            )}>
                              <span>{badge.unlocked ? (isUA ? 'Здобуто' : 'Earned') : (isUA ? 'Прогрес' : 'Progress')}</span>
                              <span>{badge.current}/{badge.target}</span>
                            </div>
                            <div className={cn(
                              "w-full h-1 rounded-full overflow-hidden",
                              isStalker ? "bg-zinc-900 border border-[#4ade80]/20" :
                              isLight ? "bg-zinc-200" : "bg-zinc-800"
                            )}>
                              <div 
                                className={cn(
                                  "h-full rounded-full transition-all duration-300",
                                  badge.unlocked 
                                    ? (isStalker ? "bg-[#4ade80]" : "bg-emerald-500") 
                                    : (isStalker ? "bg-[#4ade80]/40" : isLight ? "bg-zinc-300" : "bg-zinc-600")
                                )}
                                style={{ width: `${progressRatio}%` }}
                              />
                            </div>
                          </div>
                        </div>
                      </button>
                    );
                  })}
              </div>

            </div>
          )}

        </div>
      )}

      {/* BIO-BOX UNBOXING MODAL */}
      <BioBoxUnboxingModal
        isOpen={isUnboxingModalOpen}
        onClose={() => {
          setIsUnboxingModalOpen(false);
          setSelectedCrateForUnbox(null);
          refreshGamificationData();
        }}
        box={selectedCrateForUnbox}
        boxes={crates}
        userId={activeUid}
        lang={lang}
        vibe={vibe}
        tone={tone}
        theme={theme}
        skin={skin}
        onBoxOpened={(_payout) => {
          refreshGamificationData();
        }}
      />

    </div>
  );
};
